import React, { useEffect, useState } from 'react';
import { usePolarisRecaptcha } from '../hooks/usePolarisRecaptcha';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../utils/apiConfig';
import polarisLogo from '../images/Polaris Logo.svg';
import '../styles/checkout-page.css';
import { RecaptchaDisclosure } from './RecaptchaDisclosure';
import { apiClient } from "../utils/apiClient";

/**
 * Intermediate checkout page for the Clever subscription signup flow.
 *
 * The browser arrives here after the Clever OAuth subscribe-callback completes.
 * The backend has already exchanged the authorization code and redirected here with
 * cleverId and cleverDistrictId as URL query parameters.
 *
 * This page then needs the user to pick a plan (or reads priceId from state) and
 * initiates a Stripe checkout session, forwarding the Clever identity data as metadata
 * so the webhook can link the created account to the correct Clever identity without
 * relying on email (which Clever does not guarantee).
 */

interface CheckoutSessionResponse {
  sessionId: string;
  sessionUrl: string;
}

export const CleverCheckoutPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { executeRecaptcha } = usePolarisRecaptcha();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Plan details come from navigation state (set by SignupPage when user picks a plan)
  const state = (location.state as any) || {};
  const { priceId, billingInterval, enteredSchoolName: enteredFromState, cleverDistrictName: districtFromState, cleverSchoolName: schoolFromState, cleverSchoolId: schoolIdFromState } = state;

  // Clever identity comes from URL query params set by the backend subscribe-callback redirect
  const params = new URLSearchParams(location.search);
  const cleverId = params.get('cleverId');
  const cleverDistrictId = params.get('cleverDistrictId');
  const cleverSchoolId = params.get('cleverSchoolId') || schoolIdFromState || undefined;
  const cleverSchoolName = params.get('cleverSchoolName') || schoolFromState || undefined;
  const cleverDistrictName = params.get('cleverDistrictName') || districtFromState || undefined;
  const enteredSchoolName = enteredFromState || undefined;

  useEffect(() => {
    const createCheckoutSession = async () => {
      if (!priceId) {
        // No plan selected yet — send user back to signup to pick one.
        // We preserve the Clever params in state so the signup page can reattach them.
        navigate('/signup', {
          state: { cleverId, cleverDistrictId },
          replace: true
        });
        return;
      }

      if (!cleverId) {
        setError('Missing Clever identity. Please try again.');
        setIsLoading(false);
        return;
      }

      const schoolForCheckout = enteredSchoolName || cleverSchoolName;
      if (!schoolForCheckout || !String(schoolForCheckout).trim()) {
        setError(
          'School name is required for Clever checkout. Use Subscribe from the home page, confirm your school, then continue to payment.',
        );
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        if (!executeRecaptcha) {
          setError('Security verification is still loading. Please try again in a moment.');
          setIsLoading(false);
          return;
        }
        const recaptchaToken = await executeRecaptcha('checkout');

        const baseUrl = window.location.origin;
        const requestBody: Record<string, string> = {
          priceId,
          billingInterval: billingInterval || 'month',
          successUrl: `${baseUrl}/subscription/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${baseUrl}/subscription/cancel`,
          cleverId,
          cleverDistrictId: cleverDistrictId || '',
          recaptchaToken
        };
        if (cleverDistrictName) {
          requestBody.cleverDistrictName = cleverDistrictName;
        }
        if (cleverSchoolId) {
          requestBody.cleverSchoolId = cleverSchoolId;
        }
        if (cleverSchoolName) {
          requestBody.cleverSchoolName = cleverSchoolName;
        }
        if (enteredSchoolName) {
          requestBody.enteredSchoolName = enteredSchoolName;
        }

        const response = await apiClient(buildApiUrl('api/subscriptions/checkout'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({} as { detail?: string; message?: string }));
          const msg =
            (typeof errorData.detail === 'string' && errorData.detail) ||
            (typeof errorData.message === 'string' && errorData.message) ||
            `HTTP error ${response.status}`;
          throw new Error(msg);
        }

        const data: CheckoutSessionResponse = await response.json();

        if (!data.sessionUrl) {
          throw new Error('No checkout URL received from server');
        }

        window.location.href = data.sessionUrl;
      } catch (err) {
        console.error('Clever checkout error:', err);
        setError(
          err instanceof Error
            ? err.message
            : 'Unable to create checkout session. Please try again.'
        );
        setIsLoading(false);
      }
    };

    createCheckoutSession();
  }, [
    priceId,
    cleverId,
    cleverDistrictId,
    billingInterval,
    cleverDistrictName,
    cleverSchoolId,
    cleverSchoolName,
    enteredSchoolName,
    executeRecaptcha,
  ]);

  const handleGoBack = () => {
    navigate('/signup');
  };

  if (error) {
    return (
      <div className="checkout-page">
        <div className="checkout-container">
          <div className="checkout-error">
            <div className="polaris-logo">
              <img src={polarisLogo} alt="Polaris Athlete" />
            </div>
            <div className="error-icon">
              <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="40" cy="40" r="40" fill="#FEF2F2" />
                <path d="M40 20L60 56H20L40 20Z" fill="#FCD34D" />
                <path d="M38 36H42V48H38V36Z" fill="#1A1A1A" />
                <circle cx="40" cy="52" r="2" fill="#1A1A1A" />
              </svg>
            </div>
            <h1>Payment Setup Failed</h1>
            <div className="error-message">{error}</div>
            <div className="error-actions">
              <button className="btn-secondary" onClick={handleGoBack} data-testid="go-back-btn">
                Go Back
              </button>
            </div>
            <RecaptchaDisclosure />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <div className="checkout-loading">
          <div className="polaris-logo">
            <img src={polarisLogo} alt="Polaris Athlete" />
          </div>
          <h1>Redirecting to Secure Payment...</h1>
          <div className="spinner"></div>
          <p className="checkout-message">
            You will be redirected to Stripe Checkout to complete your subscription
          </p>
          <div className="security-badge">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0110 1.944zM11.207 8.793a1 1 0 00-1.414-1.414L8 9.172 7.207 8.38a1 1 0 00-1.414 1.414l1.5 1.5a1 1 0 001.414 0l3-3z" clipRule="evenodd" />
            </svg>
            <span>Secured by Stripe</span>
          </div>
          <RecaptchaDisclosure />
        </div>
      </div>
    </div>
  );
};

export default CleverCheckoutPage;
